/**
 * Settings → AI: one provider in the routing list. Health is derived from the
 * most recent result (see lib/aiHealth); errors are a one-line summary with
 * the raw provider message behind "Details"; the model override lives under
 * "Advanced".
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { HEALTH_LABEL, deriveHealth, summarizeFailure, type AiRole } from '@/lib/aiHealth';
import type { AiProviderStatus } from '@/lib/aiSettingsApi';

export interface InlineTestResult {
  ok: boolean;
  latency_ms?: number;
  kind?: string;
  status?: number | null;
  error_type?: string | null;
  error_message?: string | null;
}

const ROLE_LABEL: Record<AiRole, string> = { primary: 'Primary', backup: 'Backup', unused: 'Not used' };
/** `env` = ANTHROPIC_MODEL / OPENAI_MODEL in Vercel — not the built-in default. */
const MODEL_SOURCE_LABEL: Record<string, string> = { default: 'default', env: 'env', override: 'override' };

const time = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString() : '—');

function HealthPill({ health }: { health: ReturnType<typeof deriveHealth> }) {
  const tone = health === 'healthy'
    ? 'border-[#8FB560]/40 text-[#8FB560]'
    : health === 'failing'
      ? 'border-[#E0556C]/40 text-[#E0556C]'
      : 'border-border text-muted-foreground';
  return (
    <span className={`inline-flex h-5 items-center gap-1.5 whitespace-nowrap rounded border px-2 text-[11px] font-medium ${tone}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {HEALTH_LABEL[health]}
    </span>
  );
}

function ErrorLine({ summary, raw }: { summary: string; raw: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-[#E0556C]">{summary}</p>
      {raw && (
        <details className="group">
          <summary className="cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground">Details</summary>
          <pre className="mt-1 whitespace-pre-wrap break-words rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-muted-foreground">{raw}</pre>
        </details>
      )}
    </div>
  );
}

export function AiProviderRow({
  p, role, result, busy, locked, routingLocked, onTest, onMakePrimary, onUseAsBackup, onRemoveBackup, onModel,
}: {
  p: AiProviderStatus;
  role: AiRole;
  /** The Test just run from this page, if any — shown in place of the stored error. */
  result: InlineTestResult | null;
  busy: boolean;
  /** Blocks actions that CALL the provider (Test, model override) — incl. the kill switch. */
  locked: boolean;
  /** Blocks routing-only saves (order / backup) — just while another action runs. */
  routingLocked: boolean;
  onTest: () => void;
  onMakePrimary: () => void;
  onUseAsBackup: () => void;
  onRemoveBackup: () => void;
  onModel: (model: string | null) => void;
}) {
  const [model, setModel] = useState('');
  // A Test just run here is the most recent result; the snapshot catches up
  // on refresh, but the pill must never contradict the inline result.
  const health = result && p.configured ? (result.ok ? 'healthy' : 'failing') : deriveHealth(p);
  const lastChecked = Math.max(p.last_success_at ?? 0, p.last_error_at ?? 0) || null;

  // The failure to show: this page's Test result, else the stored last error
  // when the provider is currently failing.
  const failure = result && !result.ok
    ? { kind: result.kind, status: result.status, type: result.error_type, message: result.error_message }
    : !result && health === 'failing'
      ? { kind: p.last_error_kind, status: p.last_error_status, type: p.last_error_type, message: p.last_error_message }
      : null;
  const raw = failure ? [failure.type, failure.message].filter(Boolean).join(': ') || (failure.status ? `HTTP ${failure.status}` : '') : '';
  const disabled = !p.configured || busy || locked;

  return (
    <li data-testid={`ai-provider-${p.id}`} className="flex flex-col gap-3 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{p.label}</span>
            <span className="text-xs text-muted-foreground">{ROLE_LABEL[role]}</span>
            <HealthPill health={health} />
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Model</dt>
            <dd className="min-w-0 break-all font-mono text-foreground">
              {p.model} <span className="font-sans text-muted-foreground">({MODEL_SOURCE_LABEL[p.model_source] ?? p.model_source})</span>
            </dd>
            <dt className="text-muted-foreground">Key</dt>
            <dd className="min-w-0 break-all font-mono text-foreground">
              {p.key_env ?? (p.key_problem
                ? <span className="font-sans text-[#E0556C]">{p.key_problem}</span>
                : <span className="font-sans text-muted-foreground">Not set — add {p.key_env_preferred ?? 'the key'} in Vercel</span>)}
              {p.key_env && p.key_env_preferred && p.key_env !== p.key_env_preferred && (
                <span className="font-sans text-muted-foreground"> (fallback — set {p.key_env_preferred})</span>
              )}
            </dd>
            <dt className="text-muted-foreground">Last checked</dt>
            <dd className="font-mono tabular-nums text-foreground">{time(lastChecked)}</dd>
            {p.breaker_open_until && (
              <>
                <dt className="text-muted-foreground">Paused until</dt>
                <dd className="font-mono tabular-nums text-[#E0556C]">{time(p.breaker_open_until)}</dd>
              </>
            )}
          </dl>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-none sm:justify-end">
          <Button size="sm" variant="outline" onClick={onTest} disabled={disabled}>
            {busy && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            Test connection
          </Button>
          {role === 'backup' && (
            <>
              <Button size="sm" variant="outline" onClick={onMakePrimary} disabled={!p.configured || routingLocked}>Make primary</Button>
              <Button size="sm" variant="ghost" onClick={onRemoveBackup} disabled={routingLocked}>Remove backup</Button>
            </>
          )}
          {role === 'unused' && (
            <Button size="sm" variant="outline" onClick={onUseAsBackup} disabled={!p.configured || routingLocked}>Use as backup</Button>
          )}
        </div>
      </div>

      {result?.ok && (
        <p className="text-xs text-[#8FB560]">
          Connected — answered in <span className="font-mono tabular-nums">{result.latency_ms ?? '?'} ms</span>
        </p>
      )}
      {failure && <ErrorLine summary={summarizeFailure(failure, p.label)} raw={raw} />}

      <details className="text-xs">
        <summary className="cursor-pointer select-none text-muted-foreground hover:text-foreground">Advanced</summary>
        <div className="mt-2 space-y-2 rounded border border-border p-3">
          <label htmlFor={`model-${p.id}`} className="block text-muted-foreground">
            Model override <span className="text-muted-foreground/80">— default <span className="font-mono">{p.default_model}</span></span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={`model-${p.id}`}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="Leave empty to use the default"
              className="h-8 w-full font-mono text-xs sm:w-64"
              disabled={disabled}
            />
            <Button size="sm" variant="outline" disabled={disabled || !model.trim()} onClick={() => onModel(model.trim())}>
              Test and save
            </Button>
            {p.model_source === 'override' && (
              <Button size="sm" variant="ghost" disabled={busy || locked} onClick={() => onModel(null)}>Reset to default</Button>
            )}
          </div>
          <p className="text-muted-foreground">A model is saved only after a Test with it passes.</p>
        </div>
      </details>
    </li>
  );
}
